// =====================================================================
// Curated icon catalog for service-business surfaces.
//
// Stored format on a block prop (services.items[].icon,
// credentials.items[].icon, etc.):
//   - `lucide:wrench` — one of the curated lucide icons below; renderer
//                       emits inline SVG, dashboard renders via
//                       lucide-react.
//   - `🔧`            — raw emoji; renderer + dashboard pass through.
//   - any other text  — legacy free-form value; renderer + dashboard
//                       pass through as text.
//
// The catalog is intentionally small (~35) so the picker stays
// scannable. Add only when a clear SMB use case justifies it. Names
// match lucide-react component names verbatim.
// =====================================================================

export interface IconEntry {
  /** lucide-react component name in kebab-case. */
  name: string;
  /** User-facing label shown in the picker tooltip. */
  label: string;
  /** Tags used for search matching. */
  tags: readonly string[];
  /** Category for grouping in the picker. */
  category: 'trust' | 'service' | 'industry' | 'general';
}

export const CURATED_ICONS: readonly IconEntry[] = [
  // ---- Trust / credibility ----------------------------------------
  { name: 'shield-check', label: 'Insured', tags: ['insured', 'licensed', 'trust', 'verified', 'shield'], category: 'trust' },
  { name: 'award', label: 'Award', tags: ['award', 'certified', 'badge', 'best'], category: 'trust' },
  { name: 'star', label: 'Star rating', tags: ['star', 'rating', 'review', 'top', 'best'], category: 'trust' },
  { name: 'thumbs-up', label: 'Recommended', tags: ['recommended', 'like', 'approved'], category: 'trust' },
  { name: 'check-circle', label: 'Verified', tags: ['verified', 'check', 'done', 'guarantee'], category: 'trust' },
  { name: 'heart', label: 'Loved', tags: ['heart', 'loved', 'favorite', 'family'], category: 'trust' },
  { name: 'sparkles', label: 'Sparkle', tags: ['sparkle', 'magic', 'new', 'shiny'], category: 'trust' },

  // ---- General service signals ------------------------------------
  { name: 'clock', label: 'Hours / quick', tags: ['clock', 'hours', 'time', 'fast', 'quick'], category: 'service' },
  { name: 'zap', label: 'Fast service', tags: ['fast', 'zap', 'speed', 'energy', 'instant'], category: 'service' },
  { name: 'map-pin', label: 'Location', tags: ['map', 'location', 'pin', 'address', 'local'], category: 'service' },
  { name: 'phone', label: 'Phone', tags: ['phone', 'call', 'contact'], category: 'service' },
  { name: 'mail', label: 'Email', tags: ['mail', 'email', 'message', 'contact'], category: 'service' },
  { name: 'calendar', label: 'Booking', tags: ['calendar', 'booking', 'schedule', 'appointment'], category: 'service' },
  { name: 'truck', label: 'Delivery', tags: ['truck', 'delivery', 'shipping', 'mobile'], category: 'service' },
  { name: 'tag', label: 'Pricing', tags: ['tag', 'price', 'deal', 'offer'], category: 'service' },
  { name: 'gift', label: 'Promo', tags: ['gift', 'promo', 'free', 'bonus'], category: 'service' },
  { name: 'dollar-sign', label: 'Price', tags: ['dollar', 'money', 'price', 'cost'], category: 'service' },

  // ---- Industry-specific ------------------------------------------
  { name: 'wrench', label: 'Repair', tags: ['wrench', 'repair', 'fix', 'plumbing', 'maintenance'], category: 'industry' },
  { name: 'hammer', label: 'Construction', tags: ['hammer', 'construction', 'build', 'contractor'], category: 'industry' },
  { name: 'paintbrush', label: 'Painting', tags: ['paintbrush', 'paint', 'design', 'art'], category: 'industry' },
  { name: 'scissors', label: 'Salon', tags: ['scissors', 'salon', 'hair', 'cut', 'grooming'], category: 'industry' },
  { name: 'car', label: 'Auto', tags: ['car', 'auto', 'vehicle'], category: 'industry' },
  { name: 'home', label: 'Home services', tags: ['home', 'house', 'real estate'], category: 'industry' },
  { name: 'leaf', label: 'Eco / cleaning', tags: ['leaf', 'eco', 'green', 'cleaning', 'natural'], category: 'industry' },
  { name: 'coffee', label: 'Cafe', tags: ['coffee', 'cafe', 'drink', 'breakfast'], category: 'industry' },
  { name: 'utensils', label: 'Restaurant', tags: ['utensils', 'food', 'restaurant', 'dining'], category: 'industry' },
  { name: 'shopping-bag', label: 'Retail', tags: ['shopping', 'bag', 'retail', 'store'], category: 'industry' },
  { name: 'camera', label: 'Photography', tags: ['camera', 'photo', 'photography'], category: 'industry' },
  { name: 'dumbbell', label: 'Fitness', tags: ['dumbbell', 'fitness', 'gym', 'workout'], category: 'industry' },
  { name: 'baby', label: 'Childcare', tags: ['baby', 'childcare', 'daycare', 'kids'], category: 'industry' },
  { name: 'dog', label: 'Pet services', tags: ['dog', 'pet', 'animal', 'vet'], category: 'industry' },
  { name: 'briefcase', label: 'Professional', tags: ['briefcase', 'work', 'professional', 'office'], category: 'industry' },
  { name: 'droplet', label: 'Plumbing / water', tags: ['droplet', 'water', 'plumbing'], category: 'industry' },
  { name: 'flame', label: 'Hot / popular', tags: ['flame', 'fire', 'hot', 'popular'], category: 'industry' },

  // ---- General iconography ----------------------------------------
  { name: 'users', label: 'People', tags: ['users', 'people', 'team', 'family-owned'], category: 'general' },
  { name: 'lightbulb', label: 'Idea', tags: ['lightbulb', 'idea', 'tip', 'consulting'], category: 'general' },
  { name: 'key', label: 'Access', tags: ['key', 'access', 'real estate'], category: 'general' },
];

const NAMES = new Set(CURATED_ICONS.map((i) => i.name));

/** Returns true if the bare name matches a curated icon. */
export function isCuratedIconName(name: string): boolean {
  return NAMES.has(name);
}

export interface ParsedIconValue {
  kind: 'lucide' | 'text';
  /** The bare lucide icon name if kind is 'lucide'; otherwise the raw string. */
  value: string;
}

/**
 * Classify a stored icon value as either a lucide reference
 * (`lucide:foo` where `foo` is a curated name) or raw text (emoji,
 * legacy free-form value).
 */
export function parseIconValue(raw: string | null | undefined): ParsedIconValue | null {
  if (!raw) return null;
  if (raw.startsWith('lucide:')) {
    const name = raw.slice('lucide:'.length);
    if (isCuratedIconName(name)) return { kind: 'lucide', value: name };
    // Unknown lucide name — fall through as text so the renderer
    // doesn't crash, the operator just sees the literal "lucide:foo".
    return { kind: 'text', value: raw };
  }
  return { kind: 'text', value: raw };
}

export function lucideIconValue(name: string): string {
  return `lucide:${name}`;
}
